import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, MapPin, Shield, Wrench } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

interface MgmtCard {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  description: string;
}

export const ManagementGrid: React.FC = () => {
  const [cards, setCards] = useState<MgmtCard[]>([]);

  useEffect(() => {
    const fetchCounts = async () => {
      const extractCount = async (res: Response): Promise<number> => {
        if (!res.ok) return 0;
        const json = await res.json();
        if (Array.isArray(json)) return json.length;
        if (json.data && Array.isArray(json.data)) return json.data.length;
        return 0;
      };

      const [orgCount, jurCount, entCount, toolCount] = await Promise.all([
        apiFetch('/api/organizations').then(extractCount),
        apiFetch('/api/jurisdictions').then(extractCount),
        apiFetch('/api/entitlements').then(extractCount),
        apiFetch('/api/tools').then(extractCount),
      ]);

      setCards([
        {
          label: 'Organizations',
          href: '/admin/organizations',
          icon: Building2,
          count: orgCount,
          description: 'Manage organizations and their members',
        },
        {
          label: 'Jurisdictions',
          href: '/admin/jurisdictions',
          icon: MapPin,
          count: jurCount,
          description: 'Manage jurisdiction hierarchy',
        },
        {
          label: 'Entitlements',
          href: '/admin/entitlements',
          icon: Shield,
          count: entCount,
          description: 'Manage entitlements',
        },
        {
          label: 'Tool Registry',
          href: '/admin/tools',
          icon: Wrench,
          count: toolCount,
          description: 'Manage tools and access',
        },
      ]);
    };
    fetchCounts();
  }, []);

  if (cards.length === 0) return null;

  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Management
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <Link
            key={card.href}
            to={card.href}
            className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-md hover:border-gray-300 transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-50 text-gray-600 flex-shrink-0">
                <card.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">
                  {card.count.toLocaleString()}
                </p>
                <p className="text-sm font-medium text-gray-700">
                  {card.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {card.description}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};
